import {Stack} from '@mui/material';
import React, {Suspense, useEffect} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router-dom';

import {DownloadApp} from 'presentation/components/DownloadApp';
import {GlobalChatErrorSnackbar} from 'presentation/components/ErrorSnackbar/GlobalChatErrorSnackbar';
import {FullscreenLoading} from 'presentation/components/Loading/FullscreenLoading';
import {EmergencyTelehealthUnavailableRoomModal} from 'presentation/components/EmergencyTelehealthUnavailableRoomModal';

import {translations} from 'presentation/translations-new';

import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {
	CONGENERE_ROUTES,
	DEFAULT_THEME_BY_PRODUCT,
	FeaturesEnum,
	HomeEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {useIsComingFromInitialFlowStore} from 'presentation/pages/others/selection-flow/store/useIsComingFromInitialFlowStore';
import {useSelectedBeneficiaryStore} from 'presentation/pages/others/selection-flow/store/useSelectedBeneficiaryStore';
import {useFacialBiometricFeatureFlag} from 'presentation/pages/private/hooks/useFacialBiometricFeatureFlag';
import {useGetProgressQuery} from 'presentation/pages/private/profile/queries/useGetProgressQuery';
import {useChangeDataAlertStore} from 'presentation/pages/private/profile/store/changeDataAlertStore';
import {useThemeStore} from 'presentation/store/useThemeStore';
import {makeFacialBiometricConsult} from 'main/factories/usecases/others/facial-biometric/post-facial-biometric';
import {makeGetFacialBiometricIdentification} from 'main/factories/usecases/others/facial-biometric/get-facial-biometric-identification';
import {NavigationMenu} from './NavigationMenu';
import {StyledStack} from './styles';
import {useAccountInformation} from 'presentation/pages';
import {EARLY_ACCESS_ROUTES} from 'presentation/constants/earlyAccessRoutes';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {loading} = translations['pt-br'].commons;

export function PrivateTemplate() {
	const bookingFlowSteps = useBookingFlowStepsStore(state => state.bookingStep);
	const {pathname} = useLocation();
	const queryClient = useQueryClient();

	const resetSelectedBeneficiary = useSelectedBeneficiaryStore(
		state => state.resetSelectedBeneficiary,
	);
	const resetChangeDataAlertState = useChangeDataAlertStore(state => state.reset);
	const resetIsComingFromInitialFlow = useIsComingFromInitialFlowStore(
		state => state.resetIsComingFromInitialFlow,
	);
	const {isEarlyAccess, beneficiary, odontoOnly, isPPOPlan} = useAccountInformation();
	const congenereEligibility = useCongenereEligibilityStore(state => state.congenereEligibility);
	const setHasFacialBiometrics = useFacialBiometricsStore(state => state.setHasFacialBiometrics);
	const lastConsultedCdPessoa = useFacialBiometricsStore(state => state.lastConsultedCdPessoa);
	const setLastConsultedCdPessoa = useFacialBiometricsStore(
		state => state.setLastConsultedCdPessoa,
	);
	const setIsUnder16 = useFacialBiometricsStore(state => state.setIsUnder16);
	const setIsTea = useFacialBiometricsStore(state => state.setIsTea);
	const setGuardians = useFacialBiometricsStore(state => state.setGuardians);
	const {isEnabled: isFacialBiometricFeatureFlagEnabled} = useFacialBiometricFeatureFlag();

	const {isEnabled: isMultiThemeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NDI_MULTI_THEME,
	);

	const {dataHostNameState, isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {data: healthCardData} = useGetHealthCardQuery({
		enabled: isMultiThemeFeatureFlagEnabled && isGndiSaoPaulo,
	});
	const setTheme = useThemeStore(state => state.setTheme);
	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const {isEnabled: isBlockPPOFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BLOCK_PPO,
	);
	const {refetch} = useGetProgressQuery();

	const hideNavigation = pathname.includes('avaliacao') || pathname.includes('comunicados');
	const hideBottomNav = pathname === HomeEnum.ACCESS_PORTAL;
	const isCongenereRouteAllowed = CONGENERE_ROUTES.some(route => {
		const routePattern = `^${route.replace(/:[^/]+/g, '[^/]+')}$`;
		return new RegExp(routePattern).test(pathname);
	});

	useEffect(() => {
		if (
			!isFacialBiometricFeatureFlagEnabled ||
			!beneficiary?.cdPessoaC ||
			lastConsultedCdPessoa === beneficiary.cdPessoaC ||
			odontoOnly
		) {
			return;
		}

		makeGetFacialBiometricIdentification()
			.get()
			.then(identification => {
				setIsUnder16(identification?.underSixteen ?? false);
				setIsTea(identification?.autist ?? false);
				const guardians = identification?.guardians ?? [];
				setGuardians(guardians);

				if (identification?.autist) {
					setLastConsultedCdPessoa(beneficiary.cdPessoaC);

					const hasGuardianWithFacialRegistered = guardians.some(
						guardian => guardian.facialRegistered,
					);

					setHasFacialBiometrics(hasGuardianWithFacialRegistered);

					return;
				}

				makeFacialBiometricConsult()
					.post({
						fAtualizar: 'S',
						app: 'PORTAL_BENEFICIARIO',
						infAdd: '',
						carregarImagens: 'N',
						cdPessoa: beneficiary.cdPessoaC,
					})
					.then(data => {
						const hasBiometrics = data.statusBiometria.toLowerCase() === 'aprovada';
						setHasFacialBiometrics(hasBiometrics);
						setLastConsultedCdPessoa(beneficiary.cdPessoaC);
					});
			});
	}, [
		beneficiary?.cdPessoaC,
		isFacialBiometricFeatureFlagEnabled,
		setHasFacialBiometrics,
		setLastConsultedCdPessoa,
		setIsUnder16,
		setIsTea,
		setGuardians,
		odontoOnly,
	]);

	useEffect(() => {
		resetChangeDataAlertState();
		resetSelectedBeneficiary();
		resetIsComingFromInitialFlow();
	}, [pathname]);

	useEffect(() => {
		const container = document.getElementById('content-container');
		if (container) {
			container.scrollTop = 0;
		}
	}, [pathname, bookingFlowSteps]);

	useEffect(() => {
		if (isBlockPPOFeatureFlagEnabled) {
			setTheme(DEFAULT_THEME_BY_PRODUCT[dataHostNameState.product]);
			return;
		}
		if (healthCardData && isMultiThemeFeatureFlagEnabled) {
			if (isGndiSaoPaulo) {
				setTheme(healthCardData?.dsModelo?.toLocaleLowerCase() || 'hap');
				return;
			}

			setTheme(DEFAULT_THEME_BY_PRODUCT[dataHostNameState.product]);
		}
	}, [
		dataHostNameState.product,
		setTheme,
		healthCardData,
		isMultiThemeFeatureFlagEnabled,
		isGndiSaoPaulo,
		isBlockPPOFeatureFlagEnabled,
	]);

	useEffect(() => {
		if (isNewProfileFeatureFlagEnabled) refetch();

		return () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PROGRESS_PROFILE],
			});
		};
	}, []);

	if (
		(isEarlyAccess && !EARLY_ACCESS_ROUTES.includes(pathname)) ||
		(congenereEligibility && !isCongenereRouteAllowed)
	) {
		return <Navigate to={HomeEnum.HOME_PAGE} replace />;
	}

	if (
		((isGndiSaoPaulo && isPPOPlan && isBlockPPOFeatureFlagEnabled) ||
			(isNotredameSaude && !isPPOPlan)) &&
		pathname !== HomeEnum.ACCESS_PORTAL
	) {
		return <Navigate to={HomeEnum.ACCESS_PORTAL} replace />;
	}

	return (
		<Suspense fallback={<FullscreenLoading text={loading} />}>
			<Stack direction="row">
				{!hideNavigation ? <NavigationMenu hideBottomNav={hideBottomNav} /> : null}
				<StyledStack id="content-container">
					<GlobalChatErrorSnackbar />
					<DownloadApp />
					<Outlet />
					<EmergencyTelehealthUnavailableRoomModal />
				</StyledStack>
			</Stack>
		</Suspense>
	);
}
